import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useData } from "../../hooks/useData";
import GlowButton from "../ui-custom/GlowButton";
import Logo from "../ui-custom/Logo";

export default function Navbar() {
  const { data: nav } = useData("navigation");
  const { data: links } = useData("links");
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => setOpen(false), [location.pathname]);

  if (!nav) return null;
  const L = links?.labels || {};

  return (
    <header className={`sticky top-0 z-40 transition-all duration-300 ${scrolled ? "glass-strong" : "bg-transparent"}`} data-testid="navbar">
      <nav className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4">
        <Logo testId="nav-logo" />

        <div className="hidden items-center gap-1 lg:flex">
          {nav.links.map((l) => {
            const active = location.pathname === l.path;
            return (
              <Link
                key={l.path}
                to={l.path}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${active ? "text-slate-900" : "text-slate-500 hover:text-slate-900"}`}
              >
                {l.label}
              </Link>
            );
          })}
        </div>

        <div className="flex items-center gap-2">
          {links && (
            <a href={links.login} target="_blank" rel="noopener noreferrer" data-testid="nav-login"
              className="hidden rounded-full px-4 py-2 text-sm font-medium text-slate-600 transition-colors hover:text-slate-900 sm:inline">
              {L.login || "Login"}
            </a>
          )}
          <GlowButton href={links?.register} to={!links ? nav.cta.path : undefined} className="hidden sm:inline-flex" data-testid="nav-cta">
            {L.register || nav.cta.label}
          </GlowButton>
          <button onClick={() => setOpen((v) => !v)} className="text-slate-800 lg:hidden" data-testid="nav-mobile-toggle" aria-label="Menu">
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden glass-strong lg:hidden" data-testid="nav-mobile-menu"
          >
            <div className="flex flex-col gap-1 px-5 py-4">
              {nav.links.map((l) => (
                <Link key={l.path} to={l.path} className="rounded-xl px-4 py-3 text-sm font-medium text-slate-700 hover:bg-slate-100" data-testid={`nav-mobile-${l.label.toLowerCase().replace(/\s+/g, "-")}`}>
                  {l.label}
                </Link>
              ))}
              {links && (
                <a href={links.login} target="_blank" rel="noopener noreferrer" className="rounded-xl px-4 py-3 text-sm font-medium text-slate-700 hover:bg-slate-100" data-testid="nav-mobile-login">
                  {L.login || "Login"}
                </a>
              )}
              <GlowButton href={links?.register} to={!links ? nav.cta.path : undefined} className="mt-2 w-full">{L.register || nav.cta.label}</GlowButton>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
